import * as React from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useAuth } from '@/lib/auth/hooks'
import { getAuthCookie } from '@/lib/auth/session-cookie'
import {
  enrollInCertificationModule,
  fetchCertificationModule,
} from '@/lib/api/certifications'
import type { CertificationModuleDetail } from '@/lib/api/certifications'
import { useI18n } from '@/app/lib/i18n'
import { CoursePageView, findContinueTrainingId } from './CoursePageView'
import * as styles from './CoursePage.css'

// Casts below: this page is mounted under the standalone certification app's
// own route tree (src/certification-app/routes.tsx), not the main app's
// globally-registered router, so these paths aren't in the `to`/navigate
// union TypeScript sees here. See routes.tsx for why only one router can
// own that global type.
function goToTraining(
  navigate: ReturnType<typeof useNavigate>,
  courseId: string,
  lessonId: string,
) {
  navigate({ to: `/course/${courseId}/lesson/${lessonId}/quiz` } as any)
}

type CoursePageProps = {
  courseId: string
}

export function CoursePage({ courseId }: CoursePageProps) {
  const { t, tx } = useI18n()
  const navigate = useNavigate()
  const { accessToken } = useAuth()
  const token = accessToken ?? getAuthCookie('access_token')

  const [course, setCourse] = React.useState<CertificationModuleDetail | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!token) return
    setLoading(true)
    fetchCertificationModule(token, courseId)
      .then(setCourse)
      .catch((err) => setError(err?.message || t('certification.course.notFound')))
      .finally(() => setLoading(false))
  }, [token, courseId, t])

  const handleBeginOrContinue = React.useCallback(async () => {
    if (!token || !course) return
    const nextId = findContinueTrainingId(course.trainings)
    if (!nextId) return
    try {
      await enrollInCertificationModule(token, courseId)
    } catch {
      // Enrollment is best-effort; navigation still proceeds.
    }
    goToTraining(navigate, courseId, nextId)
  }, [token, course, courseId, navigate])

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.scrollArea}>
          <p style={{ padding: 48, color: '#737373' }}>{tx('shared.common.loading')}</p>
        </div>
      </div>
    )
  }

  if (error || !course) {
    return (
      <div className={styles.page}>
        <div className={styles.scrollArea}>
          <p style={{ padding: 48, color: '#737373' }}>{error || t('certification.course.notFound')}</p>
        </div>
      </div>
    )
  }

  return (
    <CoursePageView
      title={course.title}
      heroImageUrl={course.heroImageUrl}
      trainings={course.trainings}
      progressPercent={course.enrollment?.progressPercent ?? null}
      onBeginOrContinue={handleBeginOrContinue}
      onWatchVideo={(lessonId) => goToTraining(navigate, courseId, lessonId)}
      onTakeQuiz={(lessonId) => goToTraining(navigate, courseId, lessonId)}
      onStartLesson={(lessonId) => goToTraining(navigate, courseId, lessonId)}
    />
  )
}
